/**
 * Service centralisé pour la gestion des nouveaux appels clients.
 * Remplace les events window spaghetti par une API propre avec callbacks.
 *
 * Architecture:
 * - Une seule fiche peut être "active" à la fois (celle qui reçoit l'injection)
 * - Les composants s'enregistrent avec register() / unregister()
 * - NewClientCallNotification utilise checkPhoneState() et injectPhone()
 */

import { RINGOVER_NEW_CLIENT_CALL } from '../../utils/ringoverEvents';

// Callbacks enregistrés par les fiches
let activeCallbacks = null;
let phoneCheckCallbacks = null;

// Listener pour l'event socket (une seule instance)
let socketListenerActive = false;

/**
 * Enregistre une fiche comme destinataire des injections de numéro.
 * @param {Object} callbacks - Objet avec hasPhone(),
 *  onInject(phoneNumber, force), getPhoneNumber()
 * @returns {Function} - Fonction de cleanup à appeler au unmount
 */
export const registerNewClientCallTarget = (callbacks) => {
  activeCallbacks = callbacks;
  phoneCheckCallbacks = callbacks;

  // Cleanup function
  return () => {
    if (activeCallbacks === callbacks) {
      activeCallbacks = null;
      phoneCheckCallbacks = null;
    }
  };
};

/**
 * Vérifie si une fiche active a déjà un numéro de téléphone.
 * @returns {boolean|null} - true si rempli, false si vide,
 *  null si pas de fiche active
 */
export const checkPhoneState = () => {
  if (!phoneCheckCallbacks || !phoneCheckCallbacks.hasPhone) {
    return null;
  }
  return phoneCheckCallbacks.hasPhone();
};

/**
 * Récupère le numéro de téléphone actuel de la fiche active.
 * @returns {string|null}
 */
export const getCurrentPhoneNumber = () => {
  if (!phoneCheckCallbacks || !phoneCheckCallbacks.getPhoneNumber) {
    return null;
  }
  return phoneCheckCallbacks.getPhoneNumber();
};

/**
 * Injecte un numéro de téléphone dans la fiche active.
 * @param {string} phoneNumber
 * @param {boolean} force - Si true, remplace même si déjà rempli
 * @returns {boolean} - true si injecté, false si pas de fiche active ou refusé
 */
export const injectPhoneNumber = (phoneNumber, force = false) => {
  if (!activeCallbacks || !activeCallbacks.onInject) {
    return false;
  }

  // Si force=false et qu'il y a déjà un numéro, on refuse
  if (!force && activeCallbacks.hasPhone && activeCallbacks.hasPhone()) {
    return false;
  }

  activeCallbacks.onInject(phoneNumber, force);
  return true;
};

/**
 * Démarre l'écoute des events socket Ringover.
 * À appeler une seule fois (par exemple dans NewClientCallNotification).
 * @param {Function} onNewCall - Callback appelé quand un appel arrive
 * @returns {Function} - Fonction de cleanup
 */
export const startSocketListener = (onNewCall) => {
  if (socketListenerActive) {
    return () => undefined;
  }

  const handler = (e) => {
    const data = e.detail?.data;
    if (data?.phoneNumber) {
      onNewCall(data.phoneNumber, data.callId);
    }
  };

  window.addEventListener(RINGOVER_NEW_CLIENT_CALL, handler);
  socketListenerActive = true;

  return () => {
    window.removeEventListener(RINGOVER_NEW_CLIENT_CALL, handler);
    socketListenerActive = false;
  };
};

/**
 * Parse un numéro de téléphone depuis l'URL query param (?phone=...)
 * @param {string} search - location.search
 * @returns {string|null}
 */
export const getPhoneFromQuery = (search) =>
  new URLSearchParams(search).get('phone');

/**
 * Vérifie si on est sur une page de création vierge (sans ID)
 * @param {string} pathname - location.pathname
 * @returns {boolean}
 */
export const isCreationPage = (pathname) => {
  // Page de création vierge: /devis ou /intervention (sans ID après)
  const isDevisCreation = pathname === '/devis' || pathname.endsWith('/devis');
  const isInterCreation =
    pathname === '/intervention' || pathname.endsWith('/intervention');

  // Exclure les pages avec ID: /devis/123 ou /intervention/123
  const hasId = (/(devis|intervention)/).test(pathname) &&
    (/\/(devis|intervention)\/[^/]+$/).test(pathname);

  return (isDevisCreation || isInterCreation) && !hasId;
};
